export default {
  en: {
    title: "Governance",
    subtitle: "Registries, agent identities, rules, releases, marketplace, emergency stops, audit and licences.",
    tab: {
      registry: "Registry",
      policies: "Rule code",
      promotions: "Releases",
      marketplace: "Marketplace",
      killswitch: "Emergency stop",
      audit: "Audit",
      licences: "Licences",
      identities: "Agent identities",
    },
    tier: "Tier {{tier}}",
    cancel: "Cancel",
    comment: "Comment",
    col: {
      agent: "Agent",
      owner: "Owner",
      risk: "Risk tier",
      status: "Status",
      profile: "Builder",
      certification: "Certification",
      digest: "Spec digest",
      model: "Model",
      provider: "Provider",
      licence: "Licence",
      eval: "Eval status",
      arabic: "Arabic score",
      actions: "Actions",
      deployments: "Deployed",
      version: "Version",
    },
    profile: { code: "Code", nocode: "Guided builder" },
    cert: { none: "Not checked yet", bronze: "Basic check passed", silver: "Standard check passed", gold: "Thorough check passed" },
    kind: { agent: "Agent", tool: "Tool", mcp_server: "MCP server" },
    env: { dev: "Development", test: "Testing", staging: "Final check", prod: "Live" },
    registry: {
      agents: "Agents",
      models: "Models",
      notDeployed: "Not released yet",
      noModels: "No models registered in the platform registry",
      status: { active: "Active", suspended: "Suspended", retired: "Retired", deprecated: "Deprecated", rejected: "Rejected" },
      to: { active: "Reactivate", suspended: "Suspend", retired: "Retire" },
      activeTitle: "Reactivate {{agent}}",
      suspendedTitle: "Suspend {{agent}}",
      retiredTitle: "Retire {{agent}}",
      activeBody: "The agent becomes available again; the OPA bundle is republished.",
      suspendedBody: "The rules refuse new runs of a paused agent as soon as the rules package is republished.",
      retiredBody: "A retired agent cannot run or be released. Use it for agents that are no longer needed.",
      changed: "{{agent}} is now {{status}}",
      suspendAgent: "Suspend {{agent}}",
      reactivateAgent: "Reactivate {{agent}}",
      retireAgent: "Retire {{agent}}",
    },
    policies: { list: "Rule code files", readOnly: "Read-only. Rules change only through code review:", source: "Rego source of {{pkg}}" },
    promotions: {
      artefact: "Artefact",
      path: "Release",
      to: "to {{env}}",
      status: {
        pending_evals: "Waiting for evals",
        pending_approval: "Waiting for approval",
        approved: "Approved",
        completed: "Completed",
        completion_failed: "Completion failed",
        rejected: "Rejected",
        cancelled: "Cancelled",
      },
      requested: "Requested",
      approvals: "Approvals",
      count: "{{have}} of {{need}}",
      signatures: "Signed approvals",
      approve: "Approve",
      reject: "Reject",
      approveTitle: "Approve release of {{agent}}",
      rejectTitle: "Reject release of {{agent}}",
      sign: "Sign decision",
      signNote: "Your decision is signed by the audit service with your approver key, bound to this digest, and verified by the control plane (four-eyes, required count).",
      recorded: "Decision signed and recorded",
      request: "Request release",
      requestTitle: "Request a release",
      requestOk: "Request",
      requestBody: "Release a saved agent version to a stage. Release approvers sign the decision; you cannot approve your own request.",
      target: "Target stage",
      reason: "Reason (optional)",
      requested_msg: "Release of {{agent}} to {{env}} requested ({{status}})",
      yours: "Your request",
      cancel: "Cancel request",
      cancelled: "Release of {{agent}} cancelled",
      approveAria: "Approve the release of {{agent}}",
      rejectAria: "Reject the release of {{agent}}",
      cancelAria: "Cancel the release of {{agent}}",
    },
    marketplace: {
      rating: "Rated {{rating}} out of 5",
      ratings: "{{avg}} from {{n}} ratings",
      installs: "{{n}} installs",
      help: "Agents, tools and MCP servers shared between organisations. Each one shows how thoroughly the platform checked it; tools from outside companies must pass at least the standard check before they can be used.",
      none: "Nothing has been shared yet. A tool administrator can share a tool from Administration › Tools (the shop button on its card); it then appears here for others to install.",
      status: { withdrawn: "Withdrawn" },
      thirdParty: "Third party",
      rateAria: "Rate {{name}}",
      rated: "Thanks, your rating was recorded",
      noRatings: "Not rated yet",
      install: "Install",
      installed: "{{name}} installed into your organisation (agents arrive as drafts)",
      withdraw: "Withdraw",
      withdrawn: "{{name}} withdrawn",
      certify: "Certify",
      certifyAria: "Certify {{name}}",
      certifyTitle: "Certify {{name}}",
      certifyBody: "Record what was checked. The platform works out the level (bronze, silver or gold) from these checks and signs it; you never choose the level yourself.",
      evidence: "Evidence (report fingerprints)",
      evidenceHelp: "Each report's SHA-256 fingerprint, so anyone can later prove which report was used.",
      versionOf: "version {{version}}",
      share: {
        button: "Share something",
        title: "Share on the marketplace",
        help: "Other organisations on this platform can find it, rate it and install it. What you share is the saved version you pick; it never changes afterwards.",
        what: "What to share",
        toolOrMcp: "A tool or MCP server",
        which: "Which one",
        version: "Version",
        noVersion: "No saved version yet. Save a version in the Builder first.",
        titleEn: "Title (English)",
        titleAr: "Title (Arabic)",
        descEn: "Description (English)",
        descAr: "Description (Arabic)",
        ok: "Share",
        done: "{{name}} is shared",
      },
      certified: "Certification recorded: {{level}}",
      licencePassed: "Licence scan passed",
      licenceReport: "Licence report fingerprint (SHA-256)",
      sbomFormat: "Parts list (SBOM) format",
      sbom: "Parts list (SBOM) fingerprint (SHA-256)",
      vulnReport: "Vulnerability report fingerprint (SHA-256)",
      critical: "Critical findings",
      high: "High findings",
    },
    kill: {
      warning: "An emergency stop blocks new requests and cancels running ones at once; finished steps are undone where they can be. Every change is audited.",
      scope: "Scope",
      target: "Target",
      reason: "Reason",
      by: "Changed by",
      engage: "Stop",
      release: "Lift",
      engageTitle: "Turn on {{scope}} emergency stop: {{target}}",
      releaseTitle: "Lift {{scope}} emergency stop: {{target}}",
      engageBody: "All matching running requests are cancelled (finished steps are undone where they can be), and no new requests start.",
      releaseBody: "Requests may start again for this target.",
      typeToConfirm: "Type {{target}} to confirm",
      reasonLabel: "Reason (at least {{n}} characters)",
      engagedMsg: "Emergency stop is on",
      released: "Emergency stop lifted",
      globalTarget: "All organisations",
      agents: "Agents",
      none: "No emergency stop is on.",
      pickAgent: "Choose an agent",
      engageAgent: "Stop this agent",
      cancelRuns: "Also cancel its running requests (finished steps are undone where they can be)",
      pendingConfirmation: "Waiting for a second operator to confirm",
      scopes: { agent: "Agent", tenant: "Organisation", global: "Global" },
      requests: "Platform-wide emergency stop: waiting for a second operator",
      requestsHelp: "A global change takes effect only when a second operator (not the requester) confirms it before it expires.",
      action: "Action",
      requestedBy: "Requested by",
      you: "you",
      expires: "Expires",
      waitingSecond: "Waiting for a second operator",
      confirm: "Confirm",
      confirmEngageTitle: "Confirm: turn on the platform-wide emergency stop",
      confirmReleaseTitle: "Confirm: lift the platform-wide emergency stop",
      confirmBody: "Requested reason: {{reason}}",
      twoPerson: "Global changes need two platform admins: one requests, a second confirms.",
      history: "History",
      noHistory: "No emergency-stop changes recorded.",
      at: "When",
      actions: {
        engage: "Stopped",
        release: "Lifted",
        engage_requested: "Stop requested",
        release_requested: "Lift requested",
        engage_confirmed: "Stop confirmed",
        release_confirmed: "Lift confirmed",
        expired: "Request expired",
      },
      needsPlatformAdmin: "A platform admin must confirm",
    },
    audit: {
      type: "Event type",
      anyType: "Any type",
      types: {
        run: "Run",
        tool_call: "Tool call",
        policy_decision: "Rule decision",
        approval_requested: "Approval requested",
        approval_decided: "Approval decided",
        config_change: "Configuration change",
        kill_switch: "Emergency stop",
        promotion: "Release",
        memory_erase: "Memory erasure",
        agent_lifecycle: "Agent identity",
        security: "Security",
      },
      run_id: "Run ID",
      case_id: "Case ID",
      actor: "Actor",
      search: "Search",
      export: "Export evidence",
      exportReady: "Evidence bundle {{id}} is ready",
      download: "Download signed bundle",
      seq: "Seq",
      at: "Recorded",
      hash: "Hash",
      tenant: "Organisation (audit chain)",
      actor_user: "User ID",
      none: "No audit records match.",
    },
    licences: {
      allowed: "Allowed",
      excepted: "Exceptions",
      denied: "Denied",
      unknown: "Unknown",
      component: "Component",
      ecosystem: "Ecosystem",
      licence: "Licence",
      verdict: "Verdict",
      source: "Source",
      register: "Component register",
      installed: "Installed packages (control plane)",
      filter: "Filter by name or licence",
      usedAs: "Used as",
    },
    classification: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
    reason: { required: "Reason (at least {{n}} characters)", optional: "Note (optional)" },
    issues: {
      share_pick: "Choose what to share and its version.",
      title_required: "Give it a title in English and Arabic.",
      framework_required: "Choose a framework",
      period_required: "Choose the period",
      reason_short: "The reason is too short",
      confirm_mismatch: "The confirmation text does not match",
      digest_invalid: "The spec digest must be 64 hexadecimal characters",
      image_invalid: "The image digest must look like sha256:<64 hex>",
      signature_required: "A signature reference is required",
      name_invalid: "Name: 3–41 lowercase letters, digits and hyphens, starting with a letter",
      owner_required: "An owner is required",
      purpose_short: "Describe the purpose in at least 10 characters",
      expiry_too_far: "The expiry is beyond the cap for this risk tier",
      agent_required: "Choose an agent",
      version_required: "Choose a submitted version",
      sha_invalid: "Report digests must be 64 hexadecimal characters",
    },
    identities: {
      help: "Each agent runs as its own identity (a Keycloak client). It is registered, attested against the digest of a submitted spec version, and can be suspended, reactivated, have its secret rotated, or be revoked for good. Every change is audited.",
      register: "Register agent identity",
      registerTitle: "Register an agent identity",
      registered: "Registered {{agent}}; attest it to activate it",
      none: "No agent identities in this organisation.",
      client: "Client ID",
      attestation: "Attested spec",
      expires: "Expires",
      name: "Agent name",
      purpose: "Purpose",
      maxClassification: "Highest data classification",
      status: { pending_attestation: "Waiting for attestation", active: "Active", suspended: "Suspended", revoked: "Revoked", expired: "Expired" },
      attest: "Attest",
      attestAgent: "Attest {{agent}}",
      attestTitle: "Attest {{agent}}",
      attestBody: "Bind the identity to a submitted spec version and a signed worker image. An attested identity becomes active.",
      attestDone: "{{agent}} is attested and active",
      version: "Spec version",
      pickVersion: "Choose a submitted version",
      specDigest: "Spec digest (SHA-256)",
      imageDigest: "Worker image digest",
      signatureRef: "Signature reference",
      suspend: "Suspend",
      suspendAgent: "Suspend {{agent}}",
      suspendTitle: "Suspend {{agent}}",
      suspendBody: "The client is disabled and its sessions end; runs cannot obtain agent tokens until it is reactivated.",
      suspendDone: "{{agent}} is suspended",
      reactivate: "Reactivate",
      reactivateAgent: "Reactivate {{agent}}",
      reactivateTitle: "Reactivate {{agent}}",
      reactivateBody: "The client is enabled again (it needs an attestation and must not be expired).",
      reactivateDone: "{{agent}} is active again",
      rotate: "Rotate secret",
      rotateAgent: "Rotate the secret of {{agent}}",
      rotateTitle: "Rotate the secret of {{agent}}",
      rotateBody: "A new client secret is generated into OpenBao. Secrets are never shown.",
      rotateDone: "The secret of {{agent}} was rotated",
      revoke: "Revoke",
      revokeAgent: "Revoke {{agent}}",
      revokeTitle: "Revoke {{agent}}",
      revokeBody: "Revocation is permanent: the client is disabled, its sessions end and its secret is deleted.",
      revokeDone: "{{agent}} is revoked",
    },
    chain: {
      title: "Audit chain {{chain}}",
      help: "Verification recomputes every hash of the organisation's append-only chain and checks the signed checkpoints.",
      verify: "Verify chain",
      checkpoint: "Record signed checkpoint",
      checkpointed: "Signed checkpoint recorded at seq {{seq}}",
      ok: "The chain is intact",
      broken: "The chain is broken",
      status: "Status",
      intact: "Intact",
      tampered: "Tampered",
      checked: "Records checked",
      checkpoints: "Checkpoints verified",
      head: "Head hash",
      errors: "Errors",
      gaps: "Known gaps",
      gap: "Checkpoint {{seq}} can no longer be verified: its signing key was lost on {{date}} ({{reason}}). The loss is recorded in the chain.",
    },
    evidence: {
      title: "Export compliance evidence",
      create: "Create bundle",
      help: "A signed zip with the control mapping, audit-chain verification, registry inventory, releases and eval results for organisation {{tenant}}.",
      framework: "Framework",
      period: "Period",
      ready: "Evidence bundle ready: {{name}}",
      download: "Download signed bundle",
    },
  },
  ar: {
    title: "الحوكمة",
    subtitle: "السجلات وهويات الوكلاء والقواعد والإصدارات والسوق والإيقاف الطارئ والتدقيق والتراخيص.",
    tab: {
      registry: "السجل",
      policies: "نص القواعد",
      promotions: "الإصدارات",
      marketplace: "السوق",
      killswitch: "الإيقاف الطارئ",
      audit: "التدقيق",
      licences: "التراخيص",
      identities: "هويات الوكلاء",
    },
    tier: "المستوى {{tier}}",
    cancel: "إلغاء",
    comment: "تعليق",
    col: {
      agent: "الوكيل",
      owner: "المالك",
      risk: "مستوى المخاطر",
      status: "الحالة",
      profile: "المنشئ",
      certification: "الاعتماد",
      digest: "بصمة المواصفة",
      model: "النموذج",
      provider: "المزوّد",
      licence: "الترخيص",
      eval: "حالة التقييم",
      arabic: "درجة العربية",
      actions: "الإجراءات",
      deployments: "المنشور",
      version: "الإصدار",
    },
    profile: { code: "برمجي", nocode: "المنشئ الموجّه" },
    cert: { none: "لم يُفحص بعد", bronze: "اجتاز الفحص الأساسي", silver: "اجتاز الفحص القياسي", gold: "اجتاز الفحص الشامل" },
    kind: { agent: "وكيل", tool: "أداة", mcp_server: "خادم MCP" },
    env: { dev: "التطوير", test: "الاختبار", staging: "التجريبية", prod: "التشغيل الفعلي" },
    registry: {
      agents: "الوكلاء",
      models: "النماذج",
      notDeployed: "لم يُرقَّ بعد",
      noModels: "لا توجد نماذج مسجلة في سجل المنصة",
      status: { active: "نشط", suspended: "موقوف", retired: "متقاعد", deprecated: "مُهمل", rejected: "مرفوض" },
      to: { active: "إعادة التفعيل", suspended: "إيقاف", retired: "إحالة للتقاعد" },
      activeTitle: "إعادة تفعيل {{agent}}",
      suspendedTitle: "إيقاف {{agent}}",
      retiredTitle: "إحالة {{agent}} للتقاعد",
      activeBody: "يعود الوكيل متاحًا، ويُعاد نشر حزمة OPA.",
      suspendedBody: "ترفض القواعد التشغيلات الجديدة للوكيل الموقوف فور إعادة نشر حزمة القواعد.",
      retiredBody: "لا يمكن تشغيل الوكيل المتقاعد أو إطلاقه. استخدمه للوكلاء الذين لم تعد هناك حاجة إليهم.",
      changed: "أصبح {{agent}} {{status}}",
      suspendAgent: "إيقاف {{agent}}",
      reactivateAgent: "إعادة تفعيل {{agent}}",
      retireAgent: "إحالة {{agent}} للتقاعد",
    },
    policies: { list: "ملفات نص القواعد", readOnly: "للقراءة فقط. لا تتغير القواعد إلا عبر مراجعة الشيفرة:", source: "مصدر Rego للحزمة {{pkg}}" },
    promotions: {
      artefact: "العنصر",
      path: "الإطلاق",
      to: "إلى {{env}}",
      status: {
        pending_evals: "بانتظار التقييمات",
        pending_approval: "بانتظار الاعتماد",
        approved: "معتمدة",
        completed: "مكتملة",
        completion_failed: "فشل الإكمال",
        rejected: "مرفوضة",
        cancelled: "ملغاة",
      },
      requested: "الطلب",
      approvals: "الاعتمادات",
      count: "{{have}} من {{need}}",
      signatures: "الاعتمادات الموقّعة",
      approve: "اعتماد",
      reject: "رفض",
      approveTitle: "اعتماد إطلاق {{agent}}",
      rejectTitle: "رفض إطلاق {{agent}}",
      sign: "توقيع القرار",
      signNote: "توقّع خدمة التدقيق قرارك بمفتاحك كمعتمِد مربوطًا بهذه البصمة، ويتحقق منه مستوى التحكم (مبدأ الأربع أعين والعدد المطلوب).",
      recorded: "تم توقيع القرار وتسجيله",
      request: "طلب إطلاق",
      requestTitle: "طلب إطلاق",
      requestOk: "طلب",
      requestBody: "أطلق إصدار وكيل محفوظًا إلى مرحلة. يوقّع معتمدو الإصدارات القرار، ولا يمكنك اعتماد طلبك.",
      target: "المرحلة المستهدفة",
      reason: "السبب (اختياري)",
      requested_msg: "طُلب إطلاق {{agent}} إلى {{env}} ({{status}})",
      yours: "طلبك",
      cancel: "إلغاء الطلب",
      cancelled: "أُلغي إطلاق {{agent}}",
      approveAria: "اعتماد إطلاق {{agent}}",
      rejectAria: "رفض إطلاق {{agent}}",
      cancelAria: "إلغاء إطلاق {{agent}}",
    },
    marketplace: {
      rating: "التقييم {{rating}} من 5",
      ratings: "{{avg}} من {{n}} تقييمات",
      installs: "{{n}} عمليات تثبيت",
      help: "وكلاء وأدوات وخوادم MCP تتشاركها الجهات. يبيّن كل منها مدى فحص المنصة له، ويجب أن تجتاز أدوات الشركات الخارجية الفحص القياسي على الأقل قبل استخدامها.",
      none: "لم يُشارَك شيء بعد. يستطيع مسؤول الأدوات مشاركة أداة من الإدارة › الأدوات (زر المتجر في بطاقتها)، فتظهر هنا ليثبّتها الآخرون.",
      status: { withdrawn: "مسحوب" },
      thirdParty: "طرف خارجي",
      rateAria: "تقييم {{name}}",
      rated: "شكرًا، سُجّل تقييمك",
      noRatings: "لم يُقيَّم بعد",
      install: "تثبيت",
      installed: "ثُبّت {{name}} في جهتك (يصل الوكلاء كمسودات)",
      withdraw: "سحب",
      withdrawn: "سُحب {{name}}",
      certify: "اعتماد",
      certifyAria: "اعتماد {{name}}",
      certifyTitle: "اعتماد {{name}}",
      certifyBody: "سجّل ما تم فحصه. تحسب المنصة المستوى (برونزي أو فضي أو ذهبي) من هذه الفحوص وتوقّعه؛ ولا تختار المستوى بنفسك أبدًا.",
      evidence: "الأدلة (بصمات التقارير)",
      evidenceHelp: "بصمة SHA-256 لكل تقرير، ليتمكن أي شخص لاحقًا من إثبات التقرير الذي استُخدم.",
      versionOf: "الإصدار {{version}}",
      share: {
        button: "مشاركة عنصر",
        title: "المشاركة في السوق",
        help: "يمكن للجهات الأخرى في هذه المنصة العثور عليه وتقييمه وتثبيته. ما تشاركه هو الإصدار المحفوظ الذي تختاره، ولا يتغير بعد ذلك.",
        what: "ماذا تشارك",
        toolOrMcp: "أداة أو خادم MCP",
        which: "أيّها",
        version: "الإصدار",
        noVersion: "لا يوجد إصدار محفوظ بعد. احفظ إصدارًا في المنشئ أولًا.",
        titleEn: "العنوان (بالإنجليزية)",
        titleAr: "العنوان (بالعربية)",
        descEn: "الوصف (بالإنجليزية)",
        descAr: "الوصف (بالعربية)",
        ok: "مشاركة",
        done: "تمت مشاركة {{name}}",
      },
      certified: "سُجّل الاعتماد: {{level}}",
      licencePassed: "اجتاز فحص التراخيص",
      licenceReport: "بصمة SHA-256 لتقرير التراخيص",
      sbomFormat: "صيغة قائمة المكونات",
      sbom: "بصمة SHA-256 لقائمة المكونات",
      vulnReport: "بصمة SHA-256 لتقرير الثغرات",
      critical: "نتائج حرجة",
      high: "نتائج عالية",
    },
    kill: {
      warning: "يمنع الإيقاف الطارئ الطلبات الجديدة ويلغي الجارية فورًا، وتُلغى الخطوات المنجزة حيث أمكن. كل تغيير مُدقق.",
      scope: "النطاق",
      target: "الهدف",
      reason: "السبب",
      by: "غيّره",
      engage: "تفعيل",
      release: "إلغاء التفعيل",
      engageTitle: "تفعيل الإيقاف الطارئ ({{scope}}): {{target}}",
      releaseTitle: "رفع الإيقاف الطارئ ({{scope}}): {{target}}",
      engageBody: "ستُلغى كل الطلبات المطابقة الجارية (وتُلغى الخطوات المنجزة حيث أمكن)، ولن تبدأ طلبات جديدة.",
      releaseBody: "يمكن أن تبدأ الطلبات مجددًا لهذا الهدف.",
      typeToConfirm: "اكتب {{target}} للتأكيد",
      reasonLabel: "السبب ({{n}} أحرف على الأقل)",
      engagedMsg: "تم تفعيل الإيقاف الطارئ",
      released: "تم إلغاء الإيقاف الطارئ",
      globalTarget: "كل الجهات",
      agents: "الوكلاء",
      none: "لا يوجد مفتاح إيقاف مُفعّل لوكيل أو جهة.",
      pickAgent: "اختر وكيلًا",
      engageAgent: "إيقاف هذا الوكيل",
      cancelRuns: "إلغاء طلباته الجارية أيضًا (وتُلغى الخطوات المنجزة حيث أمكن)",
      pendingConfirmation: "بانتظار تأكيد مشغّل ثانٍ",
      scopes: { agent: "الوكيل", tenant: "الجهة", global: "شامل" },
      requests: "الإيقاف الطارئ الشامل: بانتظار مشغّل ثانٍ",
      requestsHelp: "لا يسري التغيير الشامل إلا عندما يؤكده مشغّل ثانٍ (غير مقدّم الطلب) قبل انتهاء صلاحيته.",
      action: "الإجراء",
      requestedBy: "طلبه",
      you: "أنت",
      expires: "ينتهي في",
      waitingSecond: "بانتظار مشغّل ثانٍ",
      confirm: "تأكيد",
      confirmEngageTitle: "تأكيد: تفعيل الإيقاف الطارئ الشامل",
      confirmReleaseTitle: "تأكيد: إلغاء الإيقاف الطارئ الشامل",
      confirmBody: "سبب الطلب: {{reason}}",
      twoPerson: "تتطلب التغييرات الشاملة مسؤولَي منصة: أحدهما يطلب والآخر يؤكد.",
      history: "السجل",
      noHistory: "لا توجد تغييرات مسجلة على الإيقاف الطارئ.",
      at: "الوقت",
      actions: {
        engage: "تفعيل",
        release: "إلغاء التفعيل",
        engage_requested: "طلب تفعيل",
        release_requested: "طلب إلغاء",
        engage_confirmed: "تأكيد التفعيل",
        release_confirmed: "تأكيد الإلغاء",
        expired: "انتهى الطلب",
      },
      needsPlatformAdmin: "يجب أن يؤكده مسؤول منصة",
    },
    audit: {
      type: "نوع الحدث",
      anyType: "أي نوع",
      types: {
        run: "تشغيل",
        tool_call: "استدعاء أداة",
        policy_decision: "قرار القواعد",
        approval_requested: "طلب اعتماد",
        approval_decided: "قرار اعتماد",
        config_change: "تغيير إعدادات",
        kill_switch: "مفتاح إيقاف",
        promotion: "إطلاق",
        memory_erase: "محو الذاكرة",
        agent_lifecycle: "هوية وكيل",
        security: "أمن",
      },
      run_id: "معرّف التشغيل",
      case_id: "معرّف الحالة",
      actor: "الفاعل",
      search: "بحث",
      export: "تصدير الأدلة",
      exportReady: "حزمة الأدلة {{id}} جاهزة",
      download: "تنزيل الحزمة الموقّعة",
      seq: "التسلسل",
      at: "وقت التسجيل",
      hash: "البصمة",
      tenant: "الجهة (سلسلة التدقيق)",
      actor_user: "معرّف المستخدم",
      none: "لا توجد سجلات تدقيق مطابقة.",
    },
    licences: {
      allowed: "مسموح",
      excepted: "استثناءات",
      denied: "مرفوض",
      unknown: "غير معروف",
      component: "المكوّن",
      ecosystem: "المنظومة",
      licence: "الترخيص",
      verdict: "الحكم",
      source: "المصدر",
      register: "سجل المكوّنات",
      installed: "الحزم المثبتة (مستوى التحكم)",
      filter: "التصفية بالاسم أو الترخيص",
      usedAs: "طريقة الاستخدام",
    },
    classification: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيّد" },
    reason: { required: "السبب ({{n}} أحرف على الأقل)", optional: "ملاحظة (اختيارية)" },
    issues: {
      share_pick: "اختر ما تشاركه وإصداره.",
      title_required: "أعطه عنوانًا بالإنجليزية والعربية.",
      framework_required: "اختر الإطار",
      period_required: "اختر الفترة",
      reason_short: "السبب قصير جدًا",
      confirm_mismatch: "نص التأكيد غير مطابق",
      digest_invalid: "يجب أن تتكون بصمة المواصفة من 64 خانة سداسية عشرية",
      image_invalid: "يجب أن تكون بصمة الصورة بالشكل sha256:<64 خانة>",
      signature_required: "مرجع التوقيع مطلوب",
      name_invalid: "الاسم: من 3 إلى 41 حرفًا لاتينيًا صغيرًا أو رقمًا أو شرطة، ويبدأ بحرف",
      owner_required: "المالك مطلوب",
      purpose_short: "صف الغرض في 10 أحرف على الأقل",
      expiry_too_far: "تاريخ الانتهاء يتجاوز الحد المسموح لمستوى المخاطر هذا",
      agent_required: "اختر وكيلًا",
      version_required: "اختر إصدارًا مُقدَّمًا",
      sha_invalid: "يجب أن تتكون بصمات التقارير من 64 خانة سداسية عشرية",
    },
    identities: {
      help: "يعمل كل وكيل بهوية خاصة به (عميل في Keycloak). تُسجَّل الهوية ثم تُوثَّق ببصمة إصدار مواصفة مُقدَّم، ويمكن إيقافها أو إعادة تفعيلها أو تدوير سرّها أو إلغاؤها نهائيًا. كل تغيير مُدقَّق.",
      register: "تسجيل هوية وكيل",
      registerTitle: "تسجيل هوية وكيل",
      registered: "سُجّلت {{agent}}؛ وثّقها لتفعيلها",
      none: "لا توجد هويات وكلاء في هذه الجهة.",
      client: "معرّف العميل",
      attestation: "المواصفة الموثّقة",
      expires: "تنتهي في",
      name: "اسم الوكيل",
      purpose: "الغرض",
      maxClassification: "أعلى تصنيف للبيانات",
      status: { pending_attestation: "بانتظار التوثيق", active: "نشطة", suspended: "موقوفة", revoked: "مُبطلة", expired: "منتهية" },
      attest: "توثيق",
      attestAgent: "توثيق {{agent}}",
      attestTitle: "توثيق {{agent}}",
      attestBody: "اربط الهوية بإصدار مواصفة مُقدَّم وبصورة عامل موقّعة. تصبح الهوية الموثّقة نشطة.",
      attestDone: "وُثّقت {{agent}} وأصبحت نشطة",
      version: "إصدار المواصفة",
      pickVersion: "اختر إصدارًا مُقدَّمًا",
      specDigest: "بصمة المواصفة (SHA-256)",
      imageDigest: "بصمة صورة العامل",
      signatureRef: "مرجع التوقيع",
      suspend: "إيقاف",
      suspendAgent: "إيقاف {{agent}}",
      suspendTitle: "إيقاف {{agent}}",
      suspendBody: "يُعطَّل العميل وتنتهي جلساته، ولا يمكن للتشغيلات الحصول على رموز الوكيل حتى إعادة تفعيله.",
      suspendDone: "أُوقفت {{agent}}",
      reactivate: "إعادة التفعيل",
      reactivateAgent: "إعادة تفعيل {{agent}}",
      reactivateTitle: "إعادة تفعيل {{agent}}",
      reactivateBody: "يُفعَّل العميل مجددًا (يتطلب توثيقًا وألا تكون الهوية منتهية).",
      reactivateDone: "عادت {{agent}} نشطة",
      rotate: "تدوير السر",
      rotateAgent: "تدوير سر {{agent}}",
      rotateTitle: "تدوير سر {{agent}}",
      rotateBody: "يُولَّد سر عميل جديد في OpenBao. لا تُعرض الأسرار أبدًا.",
      rotateDone: "دُوِّر سر {{agent}}",
      revoke: "إبطال",
      revokeAgent: "إبطال {{agent}}",
      revokeTitle: "إبطال {{agent}}",
      revokeBody: "الإبطال نهائي: يُعطَّل العميل وتنتهي جلساته ويُحذف سرّه.",
      revokeDone: "أُبطلت {{agent}}",
    },
    chain: {
      title: "سلسلة التدقيق {{chain}}",
      help: "يعيد التحقق حساب كل بصمة في سلسلة الجهة غير القابلة للتعديل ويفحص نقاط التحقق الموقّعة.",
      verify: "التحقق من السلسلة",
      checkpoint: "تسجيل نقطة تحقق موقّعة",
      checkpointed: "سُجّلت نقطة تحقق موقّعة عند التسلسل {{seq}}",
      ok: "السلسلة سليمة",
      broken: "السلسلة مكسورة",
      status: "الحالة",
      intact: "سليمة",
      tampered: "معدَّلة",
      checked: "السجلات المفحوصة",
      checkpoints: "نقاط التحقق المؤكدة",
      head: "بصمة الرأس",
      errors: "الأخطاء",
      gaps: "فجوات معروفة",
      gap: "لم يعد التحقق من نقطة التحقق {{seq}} ممكنًا: فُقد مفتاح توقيعها في {{date}} ({{reason}}). الفقد مسجّل في السلسلة.",
    },
    evidence: {
      title: "تصدير أدلة الامتثال",
      create: "إنشاء الحزمة",
      help: "ملف مضغوط موقّع يضم ربط الضوابط والتحقق من سلسلة التدقيق وجرد السجل والإصدارات ونتائج التقييم للجهة {{tenant}}.",
      framework: "الإطار",
      period: "الفترة",
      ready: "حزمة الأدلة جاهزة: {{name}}",
      download: "تنزيل الحزمة الموقّعة",
    },
  },
};
